import { useEffect, useState } from 'react';
import styles from './styles.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleLeft,
  faAngleRight,
  faExclamationCircle,
  faPen,
} from '@fortawesome/free-solid-svg-icons';
import Switch, { Option } from '../../components/common/Switch';
import { GetCarAllocationCondition } from '../../types/carAllocation';
import DayAllocations from './components/DayAllocations';
import {
  useDeleteCarAllocationMutation,
  useGetUnallocatedQuery,
  useOrderCalcAllocationMutation,
  usePostAllocationMutation,
} from '../../api/carAllocationApi';
import WeekAllocations from './components/WeekAllocations';
import { add, addDays, isValid } from 'date-fns';
import FooterControl from '../../components/common/FooterControl';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, AppState } from '../../redux/store';
import Button from '../../components/common/Button';
import { resetAll, startEditMode } from '../../redux/carAllocationEditSlice';
import LastCalculatedAt from '../../components/common/LastCalculatedAt';
import { logger } from '../../services/logger';
import UnallocatedList from './components/UnallocatedList';
import HeaderLayout from '../../components/HeaderLayout';
import FooterLayout from '../../components/FooterLayout';
import Modal from '../../components/common/Modal';
import PolicyRequired from '../../components/common/PolicyRequired';
import DateInput from '../../components/common/DateInput';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { getWorkCategoryIDByName } from '../../services/workCategory';
// 最新版と実装自体に変更はないので unstable だがそのまま使用する
import {
  unstable_useBlocker as useBlocker,
  useSearchParams,
} from 'react-router-dom';
import { clearAllocationIdToDelete } from '../../redux/confirmAllocationTaskDeletionSlice';
import { AlertDialog } from '../../components/common/AlertDialog';

type Span = 'day' | 'week';
const spanOptions: Option[] = [
  { label: '日', value: 'day' },
  { label: '週', value: 'week' },
];
const getDateLabel = (date: Date, span: Span): string => {
  if (span === 'day') {
    return format(date, 'M/d(E)', { locale: ja });
  } else {
    const f = 'yyyy/M/d(E)';
    const start = format(date, f, { locale: ja });
    const end = format(add(date, { days: 6 }), f, { locale: ja });
    return `${start}~${end}`;
  }
};
const getDateString = (date: Date) => {
  return format(date, `yyyy-MM-dd`);
};
type GetAllocationsFormCondition = Omit<
  GetCarAllocationCondition,
  'startDate' | 'endDate'
>;
const initConditions: GetAllocationsFormCondition = {
  driverName: '',
  carCategoryIds: [],
  carNo: '',
  userId: null,
};

const startDateKey = 'startDate';
const endDateKey = 'endDate';
const spanKey = 'span';
const isSpan = (span: string | null): span is Span => {
  return span === 'day' || span === 'week';
};
const useSearchDates = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawSpan = searchParams.get(spanKey);
  const setSpan = (span: string) => {
    if (!isSpan(span)) {
      return;
    }

    setSearchParams((prev) => {
      prev.set(spanKey, span);
      // span が切り替わった場合に start/endDate を再設定する
      // startDate, endDate 未設定では別の Effect が処理するため何もしない
      if (!(startDate && endDate && isValidDates)) {
        return prev;
      }
      if (span === 'day') {
        prev.set(endDateKey, getDateString(new Date(startDate)));
      } else {
        prev.set(endDateKey, getDateString(addDays(new Date(startDate), 6)));
      }
      return prev;
    });
  };
  useEffect(() => {
    if (!isSpan(rawSpan)) {
      setSpan('day');
    }
  });

  const span = isSpan(rawSpan) ? rawSpan : 'day';
  const startDate = searchParams.get(startDateKey);
  const endDate = searchParams.get(endDateKey);
  // 後ろの effect で type narrowing を活用するため設定されているかのチェックはしない
  const isValidDates =
    isValid(new Date(startDate ?? '')) && isValid(new Date(endDate ?? ''));
  // start/endDate が未設定、または不正な日付値の場合にセットする Effect
  useEffect(() => {
    // 初回のみ起こる想定
    if (!(startDate && endDate && isValidDates)) {
      setSearchParams((prev) => {
        prev.set(startDateKey, getDateString(new Date()));
        const newEndDate = span === 'day' ? new Date() : addDays(new Date(), 6);
        prev.set(endDateKey, getDateString(newEndDate));
        return prev;
      });
    }
  }, [startDate, endDate]);

  const nextDate = () => {
    setSearchParams((prev) => {
      if (!startDate || !endDate) {
        return prev;
      }

      if (span === 'day') {
        const nextDateStr = getDateString(addDays(new Date(startDate), 1));
        prev.set(startDateKey, nextDateStr);
        prev.set(endDateKey, nextDateStr);
      } else {
        const nextStartDateStr = getDateString(addDays(new Date(startDate), 7));
        const nextEndDateStr = getDateString(addDays(new Date(endDate), 7));
        prev.set(startDateKey, nextStartDateStr);
        prev.set(endDateKey, nextEndDateStr);
      }

      return prev;
    });
  };
  const beforeDate = () => {
    setSearchParams((prev) => {
      if (!startDate || !endDate) {
        return prev;
      }

      if (span === 'day') {
        const beforeDateStr = getDateString(addDays(new Date(endDate), -1));
        prev.set(startDateKey, beforeDateStr);
        prev.set(endDateKey, beforeDateStr);
      } else {
        const beforeStartDateStr = getDateString(
          addDays(new Date(startDate), -7)
        );
        const beforeEndDateStr = getDateString(addDays(new Date(endDate), -7));
        prev.set(startDateKey, beforeStartDateStr);
        prev.set(endDateKey, beforeEndDateStr);
      }

      return prev;
    });
  };
  const setDate = (date: string | Date | undefined) => {
    if (!date) {
      return;
    }

    const newStartDate = typeof date === 'string' ? new Date(date) : date;
    setSearchParams((prev) => {
      prev.set(startDateKey, getDateString(newStartDate));
      prev.set(
        endDateKey,
        getDateString(span === 'day' ? newStartDate : addDays(newStartDate, 6))
      );

      return prev;
    });
  };

  return {
    span,
    setSpan,
    startDate: startDate ?? getDateString(new Date()),
    endDate:
      endDate ??
      getDateString(span === 'day' ? new Date() : addDays(new Date(), 6)),
    nextDate,
    beforeDate,
    setDate,
  };
};

function Allocations() {
  const { span, setSpan, startDate, endDate, nextDate, beforeDate, setDate } =
    useSearchDates();
  const [lastCalculatedAt, setLastCalculatedAt] = useState('');
  const [conditions, setConditions] = useState<GetAllocationsFormCondition>({
    ...initConditions,
  });
  const updateConditions = ({
    startDate,
    // endDate は startDate から span に応じて再計算するため、更新には使用しない
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    endDate,
    ...conditions
  }: GetCarAllocationCondition) => {
    setDate(startDate);
    setConditions(conditions);
  };
  const dispatch = useDispatch<AppDispatch>();
  const { editMode: inEditMode, tasksReadyToUpdate } = useSelector(
    (state: AppState) => state.carAllocationEdit
  );
  const startEdit = () => dispatch(startEditMode());
  const onEditCancel = () => dispatch(resetAll());
  const onChangeSpan = (newSpan: string) => {
    if (newSpan !== 'day' && newSpan !== 'week') {
      return;
    }
    setConditions({ ...initConditions, page: 1 });
    setSpan(newSpan);
  };

  const { data: unallocated } = useGetUnallocatedQuery(
    {
      startDate: startDate,
      endDate: endDate,
      limit: conditions.limit,
      offset: conditions.offset,
      page: conditions.page,
    },
    {
      skip: !startDate || !endDate,
    }
  );
  const [isUnallocatedListOpened, setIsUnallocatedListOpened] = useState(false);
  const openUnallocatedList = () => setIsUnallocatedListOpened(true);
  const closeUnallocatedList = () => setIsUnallocatedListOpened(false);

  const [postAllocations] = usePostAllocationMutation();
  const readyToUpdate = useSelector(
    (state: AppState) => state.carAllocationEdit.tasksReadyToUpdate
  );
  const onConfirm = () => {
    postAllocations(readyToUpdate)
      .unwrap()
      .then(() => {
        // 成功した場合に編集モードを終了する
        dispatch(resetAll());
      })
      .catch(logger.error);
  };
  const [order] = useOrderCalcAllocationMutation();
  const onOrder = () => {
    order().unwrap().catch(logger.error);
    closeUnallocatedList();
  };

  const editAlertContent = tasksReadyToUpdate.some(
    (task) =>
      [
        getWorkCategoryIDByName('回収'),
        getWorkCategoryIDByName('保管'),
      ].includes(task.workCategory.id) &&
      task.type === 'unallocated' &&
      !task.goesStraight
  ) ? (
    <>
      <div>
        配車表が変更されました。積替保管量に変動があるため、翌日以降の配車表が再計算されます。
      </div>
      <div>変更した内容を更新してよろしいでしょうか？</div>
      <div>(キャンセルすると、変更された内容は削除されます)</div>
    </>
  ) : (
    <>
      <div>
        配車表が変更されました。変更した内容を更新してよろしいでしょうか？
      </div>
      <div>(キャンセルすると、変更された内容は削除されます)</div>
    </>
  );

  const manualEditingModalContent = (close: () => void) => (
    <div>
      <h3 className={styles.modalHead}>手動での編集は固定されません</h3>
      <div className={styles.content}>
        <div className={styles.text}>
          手動での編集後に「配車を計算」ボタンを押すと
          <br />
          システムの計算上、位置が移動する可能性があります。
          <br />
          ご注意ください。
          <br />
        </div>
        <div className={styles.controls}>
          <div className={styles.modalButton}>
            <Button variants={['cancel', 'fit']} onClick={close}>
              キャンセル
            </Button>
          </div>
          <div className={styles.modalButton}>
            <Button
              variants={['fit']}
              onClick={() => {
                startEdit();
                close();
              }}
            >
              編集する
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
  const [manualEditingModalOpen, setManualEditingModalOpen] = useState(false);
  const openManualEditingModal = () => setManualEditingModalOpen(true);
  const closeManualEditingModal = () => setManualEditingModalOpen(false);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      inEditMode && currentLocation.pathname !== nextLocation.pathname
  );

  const [editCancelAlertModalOpen, setEditCancelAlertModalOpen] =
    useState(false);
  const openEditCancelAlertModal = () => setEditCancelAlertModalOpen(true);
  const closeEditCancelAlertModal = () => {
    setEditCancelAlertModalOpen(false);
    blocker.reset?.();
  };

  const editCancelAlertModal = (
    <div>
      <h3 className={styles.modalHead}>編集内容が更新されていません</h3>
      <div className={styles.content}>
        <div className={styles.text}>
          移動すると編集中の内容は破棄されます。
          <br />
          移動してよろしいでしょうか？
          <br />
        </div>
        <div className={styles.controls}>
          <div className={styles.modalButton}>
            <Button
              variants={['cancel', 'fit']}
              onClick={closeEditCancelAlertModal}
            >
              キャンセル
            </Button>
          </div>
          <div className={styles.modalButton}>
            <Button
              variants={['fit']}
              onClick={() => {
                onEditCancel();
                blocker.proceed?.();
                setEditCancelAlertModalOpen(false);
              }}
            >
              移動する
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const [toDelete] = useDeleteCarAllocationMutation();

  const allocationIdToDelete = useSelector(
    (state: AppState) => state.confirmAllocationTaskDeletion.allocationId
  );
  const onCloseDelete = () => {
    dispatch(clearAllocationIdToDelete());
  };
  const onConfirmDelete = () => {
    if (allocationIdToDelete === null) {
      return;
    }
    toDelete(allocationIdToDelete);
  };

  const [isToastPopUp, setIsToastPopUp] = useState(false);

  return (
    <HeaderLayout
      title={
        <>
          <div className={styles.head}>
            配車表
            {span === 'day' ? (
              <DateInput
                variants={['recently']}
                value={new Date(startDate)}
                onChange={setDate}
              />
            ) : (
              <div className={styles.week}>
                {getDateLabel(new Date(startDate), span)}
              </div>
            )}
            <button className={styles.arrowButton} onClick={beforeDate}>
              <FontAwesomeIcon icon={faAngleLeft} className={styles.arrow} />
            </button>
            <button className={styles.arrowButton} onClick={nextDate}>
              <FontAwesomeIcon icon={faAngleRight} className={styles.arrow} />
            </button>
            <Switch
              name="span"
              options={spanOptions}
              onChange={onChangeSpan}
              disabled={inEditMode}
              value={span}
            />
          </div>
          <div className={styles.notation}>
            {lastCalculatedAt && (
              <LastCalculatedAt value={new Date(lastCalculatedAt)} />
            )}
            {unallocated && unallocated.total !== 0 && (
              <button
                className={styles.unallocated}
                onClick={openUnallocatedList}
              >
                <FontAwesomeIcon
                  icon={faExclamationCircle}
                  className={styles.unallocatedIcon}
                />
                {`${getDateLabel(
                  new Date(startDate),
                  span
                )}の未配車が${unallocated?.total}件あります`}
              </button>
            )}
          </div>
        </>
      }
      controls={
        <div>
          {span === 'day' && (
            <>
              <div className={styles.buttons}>
                <PolicyRequired policy={31}>
                  <div className={styles.headButton}>
                    <Button
                      variants={['secondary', 'contain']}
                      onClick={openManualEditingModal}
                      disabled={inEditMode || isToastPopUp}
                    >
                      <FontAwesomeIcon
                        icon={faPen}
                        className={styles.headButtonIcon}
                      />
                      手動編集
                    </Button>
                  </div>
                </PolicyRequired>
                <PolicyRequired policy={29}>
                  <div className={styles.headButton}>
                    <Button
                      variants={['contain']}
                      onClick={onOrder}
                      disabled={inEditMode || isToastPopUp}
                    >
                      配車を計算
                    </Button>
                  </div>
                </PolicyRequired>
              </div>
            </>
          )}
        </div>
      }
    >
      <FooterLayout
        main={
          span === 'day' ? (
            <DayAllocations
              initConditions={{ startDate, endDate, ...initConditions }}
              conditions={{ startDate, endDate, ...conditions }}
              onConditionChange={updateConditions}
              setLastCalculatedAt={setLastCalculatedAt}
              isToastPopUp={isToastPopUp}
            />
          ) : (
            <WeekAllocations
              initConditions={{ startDate, endDate, ...initConditions }}
              conditions={{ startDate, endDate, ...conditions }}
              onConditionChange={updateConditions}
              setLastCalculatedAt={setLastCalculatedAt}
              isToastPopUp={isToastPopUp}
            ></WeekAllocations>
          )
        }
        footer={
          <FooterControl setIsNeedBottomMargin={(v) => setIsToastPopUp(v)}>
            {inEditMode && (
              <div className={styles.buttons}>
                <div className={styles.button}>
                  <Button
                    onClick={openEditCancelAlertModal}
                    variants={['cancel', 'fit']}
                  >
                    キャンセル
                  </Button>
                </div>
                <div className={styles.button}>
                  <Button
                    onClick={onConfirm}
                    disabled={readyToUpdate.length === 0}
                    variants={['fit']}
                  >
                    確定
                  </Button>
                </div>
              </div>
            )}
          </FooterControl>
        }
        error={!!tasksReadyToUpdate.length && editAlertContent}
      />
      {isUnallocatedListOpened && (
        <UnallocatedList start={startDate} onClose={closeUnallocatedList} />
      )}
      {manualEditingModalOpen && (
        <Modal onRequestClose={closeManualEditingModal}>
          {manualEditingModalContent(closeManualEditingModal)}
        </Modal>
      )}
      {(editCancelAlertModalOpen || blocker.state === 'blocked') && (
        <Modal onRequestClose={closeEditCancelAlertModal}>
          {editCancelAlertModal}
        </Modal>
      )}
      {allocationIdToDelete !== null && (
        <DeleteConfirmModal
          onClose={onCloseDelete}
          onConfirm={onConfirmDelete}
        />
      )}
    </HeaderLayout>
  );
}

type DeleteConfirmModalProps = {
  onClose: () => void;
  onConfirm: () => void;
};
function DeleteConfirmModal({ onClose, onConfirm }: DeleteConfirmModalProps) {
  const goesStraight = useSelector<AppState>(
    (state) => state.confirmAllocationTaskDeletion.goesStraight
  );
  return goesStraight ? (
    <Modal onRequestClose={onClose}>
      <h3 className={styles.modalHead}>本当に削除しますか？</h3>
      <div className={styles.content}>
        <div className={styles.text}>
          削除すると、元に戻すことはできません。
        </div>
      </div>
      <div className={styles.controls}>
        <div className={styles.modalButton}>
          <Button
            type="button"
            variants={['fit', 'cancel']}
            onClick={() => {
              onClose();
            }}
          >
            キャンセル
          </Button>
        </div>
        <div className={styles.modalButton}>
          <Button
            type="button"
            variants={['fit']}
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            確定
          </Button>
        </div>
      </div>
    </Modal>
  ) : (
    <AlertDialog
      title="積替え保管量の変動があります"
      description={
        <>
          <div>
            積替え保管量に変動があるため、翌日以降の配車表が再計算されます。
          </div>
          <div>元に戻すことはできませんが、本当に削除しますか？</div>
        </>
      }
      onClose={onClose}
      onConfirm={() => {
        onConfirm();
        onClose();
      }}
      variant={['error', 'withCancel']}
    />
  );
}

export default Allocations;
